import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Flag,
  RotateCcw,
  Play,
  Pause,
  Trophy,
  Target,
  Loader2,
  BookOpen,
  Timer
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';

const ExamSimulation = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  
  // Redirect guests to upgrade page
  useEffect(() => {
    if (user?.is_guest) {
      toast.error('Exam Simulation is only available for registered users.');
      navigate('/upgrade');
    }
  }, [user, navigate]);
  
  // Track selection state
  const [selectedTrack, setSelectedTrack] = useState(user?.selected_track || 'PMP');
  
  // Exam state
  const [examStarted, setExamStarted] = useState(false);
  const [examCompleted, setExamCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  
  // Timer state
  const [timeLimit, setTimeLimit] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  
  // Results state
  const [results, setResults] = useState(null);
  
  // Dialogs
  const [showStartDialog, setShowStartDialog] = useState(true);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  
  // Question stats from database
  const [questionStats, setQuestionStats] = useState({});

  // Default track info (will be overridden by actual question counts)
  const defaultTrackInfo = {
    PMP: { name: 'PMP', fullName: 'Project Management Professional', targetQuestions: 180, time: 230, passingScore: 61 },
    CAPM: { name: 'CAPM', fullName: 'Certified Associate in Project Management', targetQuestions: 150, time: 180, passingScore: 61 },
    PSM: { name: 'PSM', fullName: 'Professional Scrum Master', targetQuestions: 80, time: 60, passingScore: 85 },
    PSPO: { name: 'PSPO', fullName: 'Professional Scrum Product Owner', targetQuestions: 80, time: 60, passingScore: 85 },
    PRINCE2: { name: 'PRINCE2', fullName: 'PRINCE2 Foundation', targetQuestions: 60, time: 60, passingScore: 55 },
    SAFe: { name: 'SAFe', fullName: 'SAFe Foundation', targetQuestions: 45, time: 90, passingScore: 77 },
    SAFE: { name: 'SAFe', fullName: 'SAFe Foundation', targetQuestions: 45, time: 90, passingScore: 77 },
    ITIL4: { name: 'ITIL4', fullName: 'ITIL 4 Foundation', targetQuestions: 40, time: 60, passingScore: 65 }
  };

  // State for available courses (for admin)
  const [allCourses, setAllCourses] = useState([]);
  const isAdmin = user?.role === 'admin';

  // Fetch available courses for admins
  useEffect(() => {
    const fetchCourses = async () => {
      if (isAdmin) {
        try {
          const response = await fetch(`${API}/categories`);
          if (response.ok) {
            const data = await response.json();
            // Extract course codes from the response - handle both formats
            const categoriesArray = data.categories || data;
            const courses = categoriesArray.map(c => c.id || c.code || c.name).filter(Boolean);
            if (courses.length > 0) {
              setAllCourses(courses);
            }
          }
        } catch (error) {
          console.error('Failed to fetch courses:', error);
        }
      }
    };
    fetchCourses();
  }, [isAdmin]);

  // Get available tracks for user
  const getAvailableTracks = () => {
    // Admins get access to all courses
    if (isAdmin && allCourses.length > 0) {
      return allCourses;
    }
    
    const userTrackAccess = user?.track_access || [];
    const activeTracks = userTrackAccess.filter(t => t.is_active).map(t => t.track);
    
    if (activeTracks.length === 0) {
      return [user?.selected_track || 'PMP'];
    }
    
    return activeTracks;
  };

  const availableTracks = getAvailableTracks();

  // Fetch question stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/questions/stats`);
        if (response.ok) {
          const stats = await response.json();
          setQuestionStats(stats);
        }
      } catch (error) {
        console.error('Failed to fetch question stats:', error);
      }
    };
    fetchStats();
  }, []);

  // Calculate actual available questions for current track
  const getTrackInfo = () => {
    const track = selectedTrack || user?.selected_track || 'PMP';
    const defaultInfo = defaultTrackInfo[track] || { 
      name: track, 
      fullName: track, 
      targetQuestions: 50, 
      time: 60, 
      passingScore: 65 
    };
    const availableQuestions = questionStats[track] || 0;
    
    // Use minimum of target and available questions
    const actualQuestions = Math.min(defaultInfo.targetQuestions, availableQuestions) || availableQuestions;
    
    // Adjust time proportionally if we have fewer questions
    const timeRatio = actualQuestions / defaultInfo.targetQuestions;
    const adjustedTime = Math.max(Math.round(defaultInfo.time * timeRatio), 10); // Minimum 10 minutes
    
    return {
      ...defaultInfo,
      questions: actualQuestions,
      time: adjustedTime,
      hasEnoughQuestions: availableQuestions >= defaultInfo.targetQuestions
    };
  };

  const currentTrack = getTrackInfo();

  // Timer effect
  useEffect(() => {
    if (examStarted && !examCompleted && !isPaused && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            // Time up - trigger submit
            toast.error('Time is up! The exam will now be evaluated.');
            // Note: submitExam will be called via the timeRemaining === 0 check below
            return 0;
          }
          // Show warning at 10 minutes
          if (prev === 600) {
            setShowTimeWarning(true);
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [examStarted, examCompleted, isPaused, timeRemaining]);
  
  // Handle time up - submit exam when timer reaches 0
  useEffect(() => {
    if (examStarted && !examCompleted && timeRemaining === 0 && Object.keys(answers).length > 0) {
      submitExam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRemaining, examStarted, examCompleted]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimeUp = async () => {
    // This function is kept for backwards compatibility but timer now handles this inline
    toast.error('Time is up! The exam will now be evaluated.');
    await submitExam();
  };

  const startExam = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/exams/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          exam_type: selectedTrack,
          mode: 'exam',
          question_count: currentTrack.questions,
          time_limit: currentTrack.time
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to start exam');
      }

      setAttemptId(data.attempt_id);
      setQuestions(data.questions);
      setTimeLimit(data.time_limit * 60); // Convert to seconds
      setTimeRemaining(data.time_limit * 60);
      setExamStarted(true);
      setShowStartDialog(false);
      
      toast.success('Exam started. Good luck!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, selectedAnswers) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: selectedAnswers
    }));
  };

  const toggleFlag = (questionId) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const submitExam = async () => {
    setLoading(true);
    try {
      // Submit all answers
      for (const [questionId, selectedAnswers] of Object.entries(answers)) {
        await fetch(`${API}/exams/${attemptId}/answer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          credentials: 'include',
          body: JSON.stringify({
            question_id: questionId,
            selected_answers: Array.isArray(selectedAnswers) ? selectedAnswers : [selectedAnswers]
          })
        });
      }

      // Complete exam
      const response = await fetch(`${API}/exams/${attemptId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          time_used: Math.round((timeLimit - timeRemaining) / 60)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to complete exam');
      }

      setResults(data);
      setExamCompleted(true);
      setShowSubmitDialog(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const answeredCount = Object.keys(answers).length;
  const unansweredCount = questions.length - answeredCount;

  // Render start dialog
  if (showStartDialog && !examStarted) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#0047AB] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Timer className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Exam Simulation</CardTitle>
            <p className="text-slate-600 dark:text-slate-400">
              Real exam conditions with timer
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Course Selection */}
            <div className="space-y-2">
              <Label>Select Course</Label>
              {availableTracks.length > 1 ? (
                <Select value={selectedTrack} onValueChange={setSelectedTrack}>
                  <SelectTrigger data-testid="exam-track-select">
                    <SelectValue placeholder="Choose a course" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTracks.map(track => (
                      <SelectItem key={track} value={track}>
                        {defaultTrackInfo[track]?.fullName || track}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="p-3 bg-[#0047AB]/5 dark:bg-[#0047AB]/10 border border-[#0047AB]/20 rounded-lg">
                  <p className="font-semibold text-[#0047AB]">
                    {currentTrack.fullName}
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl text-center">
                <BookOpen className="w-6 h-6 mx-auto mb-2 text-[#0047AB]" />
                <p className="text-2xl font-bold">{currentTrack.questions}</p>
                <p className="text-sm text-slate-500">Questions</p>
              </div>
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl text-center">
                <Clock className="w-6 h-6 mx-auto mb-2 text-[#0047AB]" />
                <p className="text-2xl font-bold">{currentTrack.time}</p>
                <p className="text-sm text-slate-500">Minutes</p>
              </div>
            </div>

            {/* Warning if not enough questions */}
            {!currentTrack.hasEnoughQuestions && currentTrack.questions > 0 && (
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <BookOpen className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-blue-800 dark:text-blue-200">Practice Mode</p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Currently {currentTrack.questions} questions available. The real {currentTrack.name} exam has {defaultTrackInfo[selectedTrack]?.targetQuestions || 180} questions.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* No questions warning */}
            {currentTrack.questions === 0 && (
              <div className="p-4 bg-red-50 dark:bg-red-950/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800 dark:text-red-200">No Questions Available</p>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      There are no questions available for this track yet. Please contact an administrator or try a different course.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800 dark:text-amber-200">Exam Conditions</p>
                  <ul className="text-sm text-amber-700 dark:text-amber-300 mt-2 space-y-1">
                    <li>• AI assistant is disabled during the exam</li>
                    <li>• You can flag questions for later review</li>
                    <li>• Timer runs automatically</li>
                    <li>• Passing score: {currentTrack.passingScore}%</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={startExam}
                disabled={loading || currentTrack.questions === 0}
                className="flex-1 bg-[#0047AB] hover:bg-[#003380]"
                data-testid="start-exam-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Start Exam
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render results
  if (examCompleted && results) {
    const passed = results.score >= currentTrack.passingScore;
    
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
              passed ? 'bg-green-100 dark:bg-green-950/30' : 'bg-red-100 dark:bg-red-950/30'
            }`}>
              {passed ? (
                <Trophy className="w-10 h-10 text-green-600" />
              ) : (
                <Target className="w-10 h-10 text-red-600" />
              )}
            </div>
            <CardTitle className={`text-2xl ${passed ? 'text-green-600' : 'text-red-600'}`}>
              {passed ? 'Bestanden!' : 'Nicht bestanden'}
            </CardTitle>
            <p className="text-slate-600 dark:text-slate-400">
              {currentTrack.fullName} Simulation
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center">
              <p className="text-5xl font-bold">{results.score}%</p>
              <p className="text-slate-500">Ergebnis (Bestehensgrenze: {currentTrack.passingScore}%)</p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl text-center">
                <p className="text-2xl font-bold text-green-600">{results.correct_count}</p>
                <p className="text-xs text-slate-500">Richtig</p>
              </div>
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl text-center">
                <p className="text-2xl font-bold text-red-600">{results.incorrect_count}</p>
                <p className="text-xs text-slate-500">Falsch</p>
              </div>
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl text-center">
                <p className="text-2xl font-bold text-[#0047AB]">{results.time_used || '-'}</p>
                <p className="text-xs text-slate-500">Minuten</p>
              </div>
            </div>

            <Progress 
              value={results.score} 
              className={`h-3 ${passed ? '[&>div]:bg-green-500' : '[&>div]:bg-red-500'}`}
            />

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowReviewDialog(true)}
                className="flex-1"
              >
                Check Answers
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-[#0047AB] hover:bg-[#003380]"
              >
                Zum Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Review Dialog */}
        <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Review Answers</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {questions.map((q, idx) => {
                const userAnswer = answers[q.question_id];
                const isCorrect = JSON.stringify(userAnswer?.sort()) === JSON.stringify(q.correct_answers?.sort());
                
                return (
                  <div key={q.question_id} className={`p-4 rounded-lg border-2 ${
                    isCorrect ? 'border-green-500 bg-green-50 dark:bg-green-950/10' : 'border-red-500 bg-red-50 dark:bg-red-950/10'
                  }`}>
                    <div className="flex items-start gap-2 mb-2">
                      {isCorrect ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium">Question {idx + 1}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{q.question_text}</p>
                      </div>
                    </div>
                    {!isCorrect && (
                      <div className="mt-2 text-sm">
                        <p className="text-red-600">Ihre Antwort: {userAnswer?.join(', ') || 'Keine'}</p>
                        <p className="text-green-600">Richtig: {q.correct_answers?.join(', ')}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Render exam
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Timer Header */}
      <header className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="text-base">
                {currentTrack.name}
              </Badge>
              <span className="text-sm text-slate-500">
                Question {currentIndex + 1} / {questions.length}
              </span>
            </div>

            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              timeRemaining < 600 ? 'bg-red-100 dark:bg-red-950/30 text-red-600' : 'bg-slate-100 dark:bg-slate-800'
            }`}>
              <Clock className="w-5 h-5" />
              <span className="font-mono text-lg font-bold">{formatTime(timeRemaining)}</span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSubmitDialog(true)}
                data-testid="submit-exam-btn"
              >
                Abgeben
              </Button>
            </div>
          </div>

          <Progress value={progress} className="mt-2 h-1" />
        </div>
      </header>

      {/* Question Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {currentQuestion && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline">{currentQuestion.question_type === 'single' ? 'Einzelauswahl' : 'Mehrfachauswahl'}</Badge>
                    {flaggedQuestions.has(currentQuestion.question_id) && (
                      <Badge className="bg-amber-500">
                        <Flag className="w-3 h-3 mr-1" />
                        Markiert
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg leading-relaxed">
                    {currentQuestion.question_text}
                  </CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => toggleFlag(currentQuestion.question_id)}
                  className={flaggedQuestions.has(currentQuestion.question_id) ? 'text-amber-500' : ''}
                  data-testid="flag-question-btn"
                >
                  <Flag className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {currentQuestion.question_type === 'single' ? (
                <RadioGroup
                  value={answers[currentQuestion.question_id]?.[0] || ''}
                  onValueChange={(value) => handleAnswer(currentQuestion.question_id, [value])}
                  className="space-y-3"
                >
                  {currentQuestion.choices?.map((choice) => (
                    <div
                      key={choice.id}
                      className="flex items-center space-x-3 p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-[#0047AB] cursor-pointer"
                    >
                      <RadioGroupItem value={choice.id} id={choice.id} />
                      <Label htmlFor={choice.id} className="flex-1 cursor-pointer">
                        {choice.text}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              ) : (
                <div className="space-y-3">
                  {currentQuestion.choices?.map((choice) => {
                    const isChecked = answers[currentQuestion.question_id]?.includes(choice.id);
                    return (
                      <div
                        key={choice.id}
                        className="flex items-center space-x-3 p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-[#0047AB] cursor-pointer"
                        onClick={() => {
                          const current = answers[currentQuestion.question_id] || [];
                          if (isChecked) {
                            handleAnswer(currentQuestion.question_id, current.filter(id => id !== choice.id));
                          } else {
                            handleAnswer(currentQuestion.question_id, [...current, choice.id]);
                          }
                        }}
                      >
                        <Checkbox checked={isChecked} />
                        <Label className="flex-1 cursor-pointer">{choice.text}</Label>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex gap-1">
            {questions.slice(Math.max(0, currentIndex - 3), currentIndex + 4).map((q, idx) => {
              const actualIndex = Math.max(0, currentIndex - 3) + idx;
              return (
                <Button
                  key={q.question_id}
                  variant={actualIndex === currentIndex ? 'default' : 'outline'}
                  size="icon"
                  className={`w-8 h-8 ${
                    flaggedQuestions.has(q.question_id) ? 'border-amber-500' : ''
                  } ${answers[q.question_id] ? 'bg-green-100 dark:bg-green-950/30' : ''}`}
                  onClick={() => setCurrentIndex(actualIndex)}
                >
                  {actualIndex + 1}
                </Button>
              );
            })}
          </div>

          <Button
            onClick={() => setCurrentIndex(prev => Math.min(questions.length - 1, prev + 1))}
            disabled={currentIndex === questions.length - 1}
          >
            Weiter
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Question Grid */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-sm">Question Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1">
              {questions.map((q, idx) => (
                <Button
                  key={q.question_id}
                  variant="outline"
                  size="icon"
                  className={`w-8 h-8 text-xs ${
                    idx === currentIndex ? 'ring-2 ring-[#0047AB]' : ''
                  } ${flaggedQuestions.has(q.question_id) ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20' : ''
                  } ${answers[q.question_id] ? 'bg-green-100 dark:bg-green-950/30 border-green-500' : ''}`}
                  onClick={() => setCurrentIndex(idx)}
                >
                  {idx + 1}
                </Button>
              ))}
            </div>
            <div className="flex gap-4 mt-4 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-100 border border-green-500 rounded"></div>
                Beantwortet ({answeredCount})
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-amber-50 border border-amber-500 rounded"></div>
                Markiert ({flaggedQuestions.size})
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-white border border-slate-300 rounded"></div>
                Offen ({unansweredCount})
              </span>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Submit Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Exam?</AlertDialogTitle>
            <AlertDialogDescription>
              You have answered {answeredCount} of {questions.length} questions.
              {unansweredCount > 0 && (
                <span className="block text-amber-600 mt-2">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  {unansweredCount} questions are still unanswered!
                </span>
              )}
              {flaggedQuestions.size > 0 && (
                <span className="block text-amber-600 mt-1">
                  <Flag className="w-4 h-4 inline mr-1" />
                  {flaggedQuestions.size} questions are flagged.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Weiter bearbeiten</AlertDialogCancel>
            <AlertDialogAction onClick={submitExam} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Time Warning Dialog */}
      <AlertDialog open={showTimeWarning} onOpenChange={setShowTimeWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Noch 10 Minuten!
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have 10 minutes left. Please make sure you have answered all questions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Verstanden</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExamSimulation;
